import { useEffect, useReducer, useRef, useState } from 'react';
import { ChevronLeft, X, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import {
  OrgOperationDecision,
  DEFAULT_ADMIN_OPERATION_TIMELOCK_SECONDS,
  DisasterRecoveryProposalAction,
} from '@mls-wallet/shared-types';
import { SECONDS_PER_DAY } from '@/lib/utils';
import { useActiveDRProposal } from '@/hooks/admin/organization/use-active-disaster-recovery-proposal';
import { countCompletedSigners } from '@/utils/SignatureCountsHelpers';
import { useCreateDisasterRecoveryProposal } from '@/hooks/admin/organization/use-create-disaster-recovery-proposal';
import { useDisasterRecoveryProposalSignature } from '@/hooks/admin/organization/use-disaster-recovery-proposal-signature';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useDisasterRecoveryAddressActivity } from '@/hooks/admin/organization/use-disaster-recovery-address-activity';
import type { ActiveDisasterRecoveryProposalResponse } from '@/hooks/admin/organization/use-active-disaster-recovery-proposal';
import {
  IntroContent,
  WhatIsDRContent,
  HowDRWorksContent,
  RisksContent,
  KeepSafeContent,
  HowToSetupContent,
  StartSetupContent,
} from './disaster-recovery-info-pages';
import {
  RisksConfirmDialog,
  WalletSecurityConfirmDialog,
  DRSuccessDialog,
  ExitConfirmDialog,
  ExistingDRProposalDialog,
} from './disaster-recovery-dialogs';
import { DisasterRecoveryLearnMoreLink } from './disaster-recovery-learn-more-link';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import { getParameterStateForAddress } from '@/components/transaction-builder/parameter-inputs/utils';

const DEFAULT_ADMIN_OPERATION_TIMELOCK_DAYS = Math.round(
  DEFAULT_ADMIN_OPERATION_TIMELOCK_SECONDS / SECONDS_PER_DAY
);
const RECOVERY_WALLET_ADDRESS_INPUT_ID = 'recovery-wallet-address';

// ---------------------------------------------------------------------------
// Step enum and reducer
// ---------------------------------------------------------------------------

/** Internal step model for the setup dialog flow. */
enum DRSetupStep {
  INTRO = 0,
  WHAT_IS_DR = 1,
  HOW_DR_WORKS = 2,
  RISKS = 3,
  KEEP_SAFE = 4,
  HOW_TO_SETUP = 5,
  START_SETUP = 6,
  WALLET = 7,
  TIMELOCK = 8,
  REVIEW = 9,
}

interface DRSetupState {
  step: DRSetupStep;
  walletAddress: string;
  timelockDays: number;
  // Inline modal state for the two confirmation dialogs
  showRisksConfirmDialog: boolean;
  showWalletSecurityConfirmDialog: boolean;
  risksChecked: boolean[];
  walletSecurityChecked: boolean[];
}

type DRSetupAction =
  | { type: 'ADVANCE'; toStep: DRSetupStep }
  | { type: 'BACK' }
  | { type: 'RESET'; startStep: DRSetupStep }
  | { type: 'SET_WALLET_ADDRESS'; address: string }
  | { type: 'SET_TIMELOCK_DAYS'; days: number }
  | { type: 'OPEN_RISKS_CONFIRM' }
  | { type: 'CLOSE_RISKS_CONFIRM' }
  | { type: 'CONFIRM_RISKS' }
  | { type: 'OPEN_WALLET_SECURITY_CONFIRM' }
  | { type: 'CLOSE_WALLET_SECURITY_CONFIRM' }
  | { type: 'CONFIRM_WALLET_SECURITY' }
  | { type: 'TOGGLE_RISKS_CHECKBOX'; index: number }
  | { type: 'TOGGLE_WALLET_SECURITY_CHECKBOX'; index: number };

/** Returns the previous setup step while clamping at the intro step. */
function getPreviousDRSetupStep(step: DRSetupStep): DRSetupStep {
  switch (step) {
    case DRSetupStep.INTRO:
    case DRSetupStep.WHAT_IS_DR:
      return DRSetupStep.INTRO;
    case DRSetupStep.HOW_DR_WORKS:
      return DRSetupStep.WHAT_IS_DR;
    case DRSetupStep.RISKS:
      return DRSetupStep.HOW_DR_WORKS;
    case DRSetupStep.KEEP_SAFE:
      return DRSetupStep.RISKS;
    case DRSetupStep.HOW_TO_SETUP:
      return DRSetupStep.KEEP_SAFE;
    case DRSetupStep.START_SETUP:
      return DRSetupStep.HOW_TO_SETUP;
    case DRSetupStep.WALLET:
      return DRSetupStep.START_SETUP;
    case DRSetupStep.TIMELOCK:
      return DRSetupStep.WALLET;
    case DRSetupStep.REVIEW:
      return DRSetupStep.TIMELOCK;
    default:
      return DRSetupStep.INTRO;
  }
}

/**
 * Creates the initial state for the setup dialog flow.
 */
function makeInitialState(startStep: DRSetupStep): DRSetupState {
  return {
    step: startStep,
    walletAddress: '',
    timelockDays: 3,
    showRisksConfirmDialog: false,
    showWalletSecurityConfirmDialog: false,
    risksChecked: [false, false, false, false],
    walletSecurityChecked: [false, false, false, false],
  };
}

/**
 * Reducer function that determines the next step in the data based on the current state and action
 */
function drSetupReducer(
  state: DRSetupState,
  action: DRSetupAction
): DRSetupState {
  switch (action.type) {
    case 'ADVANCE':
      return { ...state, step: action.toStep };
    case 'BACK':
      return {
        ...state,
        step: getPreviousDRSetupStep(state.step),
      };
    case 'RESET':
      return makeInitialState(action.startStep);
    case 'SET_WALLET_ADDRESS':
      return { ...state, walletAddress: action.address };
    case 'SET_TIMELOCK_DAYS':
      return { ...state, timelockDays: action.days };
    case 'OPEN_RISKS_CONFIRM':
      return {
        ...state,
        showRisksConfirmDialog: true,
        risksChecked: [false, false, false, false],
      };
    case 'CLOSE_RISKS_CONFIRM':
      return { ...state, showRisksConfirmDialog: false };
    case 'CONFIRM_RISKS':
      return {
        ...state,
        showRisksConfirmDialog: false,
        step: DRSetupStep.KEEP_SAFE,
      };
    case 'OPEN_WALLET_SECURITY_CONFIRM':
      return {
        ...state,
        showWalletSecurityConfirmDialog: true,
        walletSecurityChecked: [false, false, false, false],
      };
    case 'CLOSE_WALLET_SECURITY_CONFIRM':
      return { ...state, showWalletSecurityConfirmDialog: false };
    case 'CONFIRM_WALLET_SECURITY':
      return {
        ...state,
        showWalletSecurityConfirmDialog: false,
        step: DRSetupStep.HOW_TO_SETUP,
      };
    case 'TOGGLE_RISKS_CHECKBOX': {
      const updated = [...state.risksChecked];
      updated[action.index] = !updated[action.index];
      return { ...state, risksChecked: updated };
    }
    case 'TOGGLE_WALLET_SECURITY_CHECKBOX': {
      const updated = [...state.walletSecurityChecked];
      updated[action.index] = !updated[action.index];
      return { ...state, walletSecurityChecked: updated };
    }
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

interface DRProgressBarProps {
  /** How many of the total segments are filled */
  filledCount: number;
  /** Total number of segments */
  totalSegments: number;
}

/**
 * Progress bar that shows at the top of the dialog flow. Fills up as pages progress
 */
function DRProgressBar({ filledCount, totalSegments }: DRProgressBarProps) {
  return (
    <div className="bg-white border-b border-border flex items-start overflow-hidden shrink-0 w-full h-[3px]">
      {Array.from({ length: totalSegments }).map((_, i) => (
        <div key={i} className="flex flex-1 items-center self-stretch">
          {i < filledCount && <div className="bg-slate-800 flex-1 h-full" />}
        </div>
      ))}
    </div>
  );
}

/**
 * Returns progress bar config for the current step.
 * Education pages (2–6) use 5 segments; setup pages (8–10) use 3 segments.
 * Intro and Start-setup have no progress bar.
 */
function getProgressConfig(
  step: DRSetupStep
): { filledCount: number; totalSegments: number } | null {
  // Case: no progress bar on the intro/start-setup pages
  if (step === DRSetupStep.INTRO || step === DRSetupStep.START_SETUP) {
    return null;
  }
  if (step <= DRSetupStep.HOW_TO_SETUP) {
    // Education phase: 5 segments, filled count = step value (1-indexed by enum)
    return { filledCount: step, totalSegments: 5 };
  }
  // Setup phase: 3 segments
  return {
    filledCount: step - DRSetupStep.WALLET + 1,
    totalSegments: 3,
  };
}

/** Returns the header title for the current step. */
function getStepTitle(step: DRSetupStep): string {
  switch (step) {
    case DRSetupStep.INTRO:
      return 'Setup disaster recovery';
    case DRSetupStep.WHAT_IS_DR:
      return 'What is disaster recovery?';
    case DRSetupStep.HOW_DR_WORKS:
      return 'How does disaster recovery work?';
    case DRSetupStep.RISKS:
      return 'What are the risks of disaster recovery?';
    case DRSetupStep.KEEP_SAFE:
      return 'How to keep the recovery wallet safe';
    case DRSetupStep.HOW_TO_SETUP:
      return 'How to setup disaster recovery';
    case DRSetupStep.START_SETUP:
      return 'Start disaster recovery setup';
    case DRSetupStep.WALLET:
      return 'Disaster recovery wallet';
    case DRSetupStep.TIMELOCK:
      return 'Disaster recovery timelock duration';
    case DRSetupStep.REVIEW:
      return 'Review your settings';
    default:
      return 'Setup disaster recovery';
  }
}

// ---------------------------------------------------------------------------
// Setup phase page content (pages 8–10 — accept form props from parent state)
// ---------------------------------------------------------------------------

interface WalletContentProps {
  walletAddress: string;
  onAddressChange: (address: string) => void;
  /** Whether the entered address has on-chain activity (warning state) */
  walletHasActivity?: boolean;
  walletActivityUnknown?: boolean;
}

/** Page 8: Recovery wallet address entry */
function WalletContent({
  walletAddress,
  onAddressChange,
  walletHasActivity = false,
  walletActivityUnknown = false,
}: WalletContentProps) {
  const walletAddressState = getParameterStateForAddress(walletAddress);
  const checksummedAddress =
    walletAddressState.isValid &&
    typeof walletAddressState.parsedValue === 'string'
      ? walletAddressState.parsedValue
      : '';

  return (
    <div className="flex flex-col gap-12 w-full">
      <div className="flex flex-col gap-2.5 w-full">
        <p className="font-medium text-zinc-800 text-xl leading-7">
          What recovery wallet do you want to use for disaster recovery?
        </p>
        <p className="text-sm text-zinc-500 leading-normal">
          The recovery wallet will be able to bypass the Guardian and the policy
          engine and execute transactions on behalf of your organization.
        </p>
      </div>

      <div className="flex flex-col gap-2.5 w-full">
        <Label
          htmlFor={RECOVERY_WALLET_ADDRESS_INPUT_ID}
          className="text-xs font-semibold text-zinc-500 uppercase tracking-wide"
        >
          Recovery wallet address
        </Label>
        <div className="bg-zinc-50 border border-white rounded-full shadow-sm p-1.5">
          <div
            className={`bg-white rounded-full flex items-center px-5 py-2.5 gap-2.5 border ${
              !walletAddressState.isValid && walletAddress !== ''
                ? 'border-red-300'
                : 'border-zinc-100'
            }`}
          >
            <Input
              id={RECOVERY_WALLET_ADDRESS_INPUT_ID}
              value={walletAddress}
              onChange={(e) => onAddressChange(e.target.value)}
              placeholder="Enter recovery wallet's address"
              aria-invalid={!walletAddressState.isValid && walletAddress !== ''}
              aria-label="Recovery wallet address"
              className={`w-full bg-transparent border-none shadow-none font-mono text-base p-0 h-auto outline-none ${
                !walletAddressState.isValid && walletAddress !== ''
                  ? 'text-red-500 placeholder:text-red-300'
                  : 'text-zinc-500 placeholder:text-zinc-400'
              }`}
            />
            {walletAddress && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onAddressChange('')}
                className="size-8 text-zinc-600 hover:text-zinc-900 shrink-0"
                aria-label="Clear address"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {checksummedAddress && checksummedAddress !== walletAddress && (
          <p className="text-xs text-zinc-500 px-1">
            Checksummed: <span className="font-mono">{checksummedAddress}</span>
          </p>
        )}
        {!walletAddressState.isValid && walletAddress !== '' && (
          <p className="text-xs text-red-500 px-1">Enter a valid address</p>
        )}

        {/* Case: wallet has existing on-chain activity, show usage warning */}
        {walletHasActivity && (
          <div className="relative mt-1 flex w-full flex-col items-start gap-[5px] overflow-hidden rounded-[10px] border border-[#FDE68A] bg-[#FFF7ED] pl-5 pr-[30px] py-5">
            <div className="flex items-center gap-[10px]">
              <AlertTriangle className="size-5 shrink-0 text-[#78350F] opacity-70" />
              <p className="text-base font-medium leading-normal text-[#78350F]">
                This wallet has been used before
              </p>
            </div>
            <div className="flex w-full items-start gap-[10px]">
              <div className="w-6 shrink-0 self-stretch" />
              <p className="w-full text-sm font-normal leading-normal text-[#9A3412]">
                It is recommended to use a wallet that is solely dedicated to
                MLS Disaster recovery and hasn&apos;t been used before.
              </p>
            </div>
          </div>
        )}
        {!walletHasActivity && walletActivityUnknown && (
          <div className="relative mt-1 flex w-full flex-col items-start gap-[5px] overflow-hidden rounded-[10px] border border-[#FDE68A] bg-[#FFF7ED] pl-5 pr-[30px] py-5">
            <div className="flex items-center gap-[10px]">
              <AlertTriangle className="size-5 shrink-0 text-[#78350F] opacity-70" />
              <p className="text-base font-medium leading-normal text-[#78350F]">
                Wallet activity could not be verified
              </p>
            </div>
            <div className="flex w-full items-start gap-[10px]">
              <div className="w-6 shrink-0 self-stretch" />
              <p className="w-full text-sm font-normal leading-normal text-[#9A3412]">
                We recommend using a wallet that is solely dedicated to MLS
                Disaster Recovery and hasn&apos;t been used before.
                <br />
                <br />
                We could not check on every deployed network. Please confirm
                that this wallet has not been used for anything else.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface TimelockContentProps {
  timelockDays: number;
  onTimelockChange: (days: number) => void;
}

/** Page 9: Timelock duration selector */
function TimelockContent({
  timelockDays,
  onTimelockChange,
}: TimelockContentProps) {
  const dayOptions = Array.from({ length: 29 }, (_, i) => i + 2); // 2 to 30

  return (
    <div className="flex flex-col gap-12 w-full">
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-col gap-2.5 w-full">
          <p className="font-medium text-zinc-800 text-xl leading-7">
            How long of a timelock do you want before triggering disaster
            recovery?
          </p>
          <p className="text-sm text-zinc-500 leading-normal">
            The recovery wallet will be able to bypass the Guardian and the
            policy engine and execute transactions on behalf of your
            organization.
          </p>
        </div>
        <DisasterRecoveryLearnMoreLink />
      </div>

      {/* Break out of dialog px-4 so the pill can extend left of the label while the label stays aligned */}
      <div className="-mx-4 flex max-w-[calc(100%+2rem)] flex-col gap-2.5 pl-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Timelock duration
        </p>
        <div className="-ml-6 w-fit max-w-none rounded-full border border-white bg-zinc-50 p-1.5 shadow-sm">
          <div className="flex w-fit shrink-0 items-center gap-2.5 rounded-full border border-zinc-100 bg-white py-2.5 pl-10 pr-10">
            <div className="flex shrink-0 items-center gap-0.5">
              <span className="whitespace-nowrap text-base font-medium text-zinc-800">
                {timelockDays} {timelockDays === 1 ? 'day' : 'days'}
              </span>
              <Select
                value={String(timelockDays)}
                onValueChange={(v) => onTimelockChange(Number(v))}
              >
                <SelectTrigger
                  aria-label="Timelock duration in days"
                  className="h-auto w-auto min-w-7 shrink-0 gap-0 border-none px-1 py-1 shadow-none focus:ring-0 [&_svg]:size-4 [&_svg]:opacity-50"
                >
                  <span className="sr-only">
                    {timelockDays} {timelockDays === 1 ? 'day' : 'days'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {dayOptions.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} {d === 1 ? 'day' : 'days'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="whitespace-nowrap text-sm text-zinc-500">
              Recovery wallet must wait {timelockDays}{' '}
              {timelockDays === 1 ? 'day' : 'days'} after initiating recovery to
              execute transactions
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ReviewContentProps {
  walletAddress: string;
  timelockDays: number;
  /** From org settings — drives the "X other admins" copy */
  numberOfApprovalsNeeded: number;
}

/** Page 10: Review disaster recovery settings before submitting */
function ReviewContent({
  walletAddress,
  timelockDays,
  numberOfApprovalsNeeded,
}: ReviewContentProps) {
  const othersText =
    numberOfApprovalsNeeded > 1
      ? ` and ${numberOfApprovalsNeeded - 1} other admin${numberOfApprovalsNeeded - 1 !== 1 ? 's' : ''}`
      : '';

  return (
    <div className="flex flex-col gap-12 w-full">
      <div className="flex flex-col gap-2.5 w-full">
        <p className="font-medium text-zinc-800 text-xl leading-7">
          Review your disaster recovery settings below
        </p>
        <p className="text-sm text-zinc-500 leading-normal">
          These settings are permanent and cannot be changed after setup is
          finalized.
        </p>
      </div>

      <div className="flex flex-col gap-2.5 w-full">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
          Recovery wallet address
        </p>
        <div className="bg-zinc-50 border border-white rounded-full shadow-sm p-1.5">
          <div className="bg-white border border-zinc-100 rounded-full flex items-center px-10 py-4">
            <p className="font-mono text-base text-zinc-500 break-all">
              {walletAddress}
            </p>
          </div>
        </div>
      </div>

      <div className="-mx-4 flex max-w-[calc(100%+2rem)] flex-col gap-2.5 pl-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Timelock duration
        </p>
        <div className="-ml-6 w-fit max-w-none rounded-full border border-white bg-zinc-50 p-1.5 shadow-sm">
          <div className="flex w-fit shrink-0 items-center gap-2.5 rounded-full border border-zinc-100 bg-white py-4 pl-10 pr-10">
            <span className="whitespace-nowrap text-base font-medium text-zinc-800">
              {timelockDays} {timelockDays === 1 ? 'day' : 'days'}
            </span>
            <span className="whitespace-nowrap text-sm text-zinc-500">
              Recovery wallet must wait {timelockDays}{' '}
              {timelockDays === 1 ? 'day' : 'days'} after initiating recovery to
              execute transactions
            </span>
          </div>
        </div>
      </div>

      <p className="text-sm text-zinc-500 leading-normal">
        You{othersText} will need to approve this operation on your mobile
        device{numberOfApprovalsNeeded > 1 ? 's' : ''} before this change takes
        effect.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main DisasterRecoverySetupDialog
// ---------------------------------------------------------------------------

interface DisasterRecoverySetupDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active proposal from the parent — null means no proposal exists yet */
  activeProposal: ActiveDisasterRecoveryProposalResponse;
  /**
   * 'setup' (default): starts at the intro page, for admins creating a new proposal.
   * 'review': starts at the education pages (skips intro), for admins reviewing an
   * existing proposal. Page 6 shows "Review proposal" which calls onReviewProposal.
   */
  mode?: 'setup' | 'review';
  /**
   * Called when the user clicks "Review proposal" on page 6 in review mode.
   * The settings page uses this to open the DRProposalReviewDialog.
   */
  onReviewProposal?: () => void;
  /** Incrementing signal from parent to open success after external initiation-review signing. */
  externalSuccessSignal?: number;
}

/**
 * Full-page disaster recovery setup dialog.
 *
 * Manages a 10-step flow (education + setup) via useReducer. Confirmation
 * dialogs gate progress after pages 4 and 5. Once settings are reviewed on
 * page 10, the user signs on mobile via PendingSignaturesModal and a success
 * dialog is shown.
 *
 */
export function DisasterRecoverySetupDialog({
  open,
  onClose,
  activeProposal,
  mode = 'setup',
  onReviewProposal,
  externalSuccessSignal = 0,
}: DisasterRecoverySetupDialogProps) {
  // Review mode skips the intro page and starts at the first education page
  const startStep =
    mode === 'review' ? DRSetupStep.WHAT_IS_DR : DRSetupStep.INTRO;
  const [state, dispatch] = useReducer(
    drSetupReducer,
    startStep,
    makeInitialState
  );
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showExistingProposalDialog, setShowExistingProposalDialog] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const wasOpenRef = useRef(false);

  const { createProposal } = useCreateDisasterRecoveryProposal();
  const { signProposal } = useDisasterRecoveryProposalSignature();
  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();
  const initiationRefreshInterval = isPendingModalOpen ? 1000 : 0;
  const { activeProposal: polledProposal, refresh: refreshProposal } =
    useActiveDRProposal(
      DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY,
      {
        // Poll while the pending-signatures modal is open so signed/cancelled state
        // is reflected directly in this dialog's polledSignables prop.
        refreshInterval: initiationRefreshInterval,
      }
    );
  const { organization } = useMyOrganization();
  const {
    hasBeenUsed: walletHasActivity,
    isUsageUnknown: walletActivityUnknown,
  } = useDisasterRecoveryAddressActivity(
    state.step === DRSetupStep.WALLET ? state.walletAddress : ''
  );
  const numberOfApprovalsNeeded = organization?.numberOfApprovalsNeeded ?? 3;
  const latestActiveProposal = polledProposal ?? activeProposal;

  const hasExistingProposal = Boolean(latestActiveProposal?.proposal);
  const existingApprovalCount = countCompletedSigners(
    latestActiveProposal?.approvals ?? []
  );
  const requiredApprovals = organization?.numberOfApprovalsNeeded ?? 3;

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    // Only reset on close->open transition so signed-state updates cannot
    // reinitialize the flow before the success dialog is shown.
    dispatch({ type: 'RESET', startStep });
    setShowExitConfirm(false);
    setShowExistingProposalDialog(false);
    setIsSubmitting(false);
    setShowSuccess(false);
    setPendingSignableIds([]);
    setPendingDecision(null);
    setIsPendingModalOpen(false);
  }, [
    open,
    startStep,
    setPendingSignableIds,
    setPendingDecision,
    setIsPendingModalOpen,
  ]);

  useEffect(() => {
    if (!open) return;
    if (externalSuccessSignal === 0) return;
    // Trigger success UI when initiation review is completed in the stacked
    // proposal-review dialog opened from this setup flow.
    setShowSuccess(true);
  }, [open, externalSuccessSignal]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const handleContinue = () => {
    switch (state.step) {
      case DRSetupStep.INTRO:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.WHAT_IS_DR });
        break;
      case DRSetupStep.WHAT_IS_DR:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.HOW_DR_WORKS });
        break;
      case DRSetupStep.HOW_DR_WORKS:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.RISKS });
        break;
      case DRSetupStep.RISKS:
        // Show confirmation dialog before advancing to page 5
        dispatch({ type: 'OPEN_RISKS_CONFIRM' });
        break;
      case DRSetupStep.KEEP_SAFE:
        // Show confirmation dialog before advancing to page 6
        dispatch({ type: 'OPEN_WALLET_SECURITY_CONFIRM' });
        break;
      case DRSetupStep.HOW_TO_SETUP:
        if (hasExistingProposal) {
          // Case: another admin already created the proposal — navigate to review it
          handleReviewExistingProposal();
        } else {
          dispatch({ type: 'ADVANCE', toStep: DRSetupStep.START_SETUP });
        }
        break;
      case DRSetupStep.START_SETUP:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.WALLET });
        break;
      case DRSetupStep.WALLET:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.TIMELOCK });
        break;
      case DRSetupStep.TIMELOCK:
        dispatch({ type: 'ADVANCE', toStep: DRSetupStep.REVIEW });
        break;
      case DRSetupStep.REVIEW:
        handleSubmitAndSign();
        break;
    }
  };

  const handleBack = () => {
    // Clamp to the initial step so review-mode users can't navigate back past WHAT_IS_DR
    if (state.step > startStep) {
      dispatch({ type: 'BACK' });
    }
  };

  /**
   * Navigates the user to the existing DR proposal for review/approval.
   * In review mode, calls the parent callback to open the proposal review dialog.
   * In setup mode (shouldn't happen when proposal exists), closes this dialog.
   */
  const handleReviewExistingProposal = () => {
    if (onReviewProposal) {
      onReviewProposal();
    } else {
      onClose();
    }
  };

  const handleCloseAttempt = () => {
    // Once setup inputs begin, require explicit exit confirmation from the top-right close action.
    if (state.step >= DRSetupStep.START_SETUP) {
      setShowExitConfirm(true);
      return;
    }
    onClose();
  };

  /**
   * Creates the DR proposal (if one doesn't already exist), creates an approval
   * signature for the current admin, then opens the PendingSignaturesModal.
   */
  const handleSubmitAndSign = async () => {
    setIsSubmitting(true);
    try {
      let proposalId: string;

      const walletAddressState = getParameterStateForAddress(
        state.walletAddress.trim()
      );
      if (
        !walletAddressState.isValid ||
        typeof walletAddressState.parsedValue !== 'string'
      ) {
        throw new Error('Recovery wallet address must be a valid address');
      }

      // Case: another admin already created a proposal - require explicit review
      if (latestActiveProposal?.proposal) {
        setShowExistingProposalDialog(true);
        return;
      }

      const proposal = await createProposal({
        recoveryWalletAddress: walletAddressState.parsedValue,
        timelockDays: state.timelockDays,
      });
      if (!proposal) throw new Error('Failed to create proposal');
      if (proposal.alreadyExists) {
        setShowExistingProposalDialog(true);
        return;
      }
      proposalId = proposal.proposal.id;

      // Create the admin's approval signature on the proposal
      await signProposal({
        proposalId,
        decision: OrgOperationDecision.APPROVE,
      });

      // Refresh so the pending modal can poll updated signature state
      await refreshProposal();

      setPendingSignableIds([proposalId]);
      setPendingDecision(OrgOperationDecision.APPROVE);
      setIsPendingModalOpen(true);
    } catch (err) {
      console.error('Failed to submit disaster recovery proposal:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSigningComplete = (result?: 'signed' | 'cancelled') => {
    resetPendingState();
    // Show completion only for a confirmed mobile signature.
    if (result === 'signed') {
      setShowSuccess(true);
    }
  };

  const handleReturnToSettings = () => {
    setShowSuccess(false);
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Footer button label
  // ---------------------------------------------------------------------------

  const getContinueLabel = () => {
    if (state.step === DRSetupStep.INTRO) return 'Get Started';
    if (state.step === DRSetupStep.START_SETUP) return 'Begin setup';
    if (state.step === DRSetupStep.REVIEW) return 'Continue to mobile approval';
    if (state.step === DRSetupStep.HOW_TO_SETUP && hasExistingProposal) {
      return 'Review proposal';
    }
    return 'Continue';
  };

  const isContinueDisabled = () => {
    if (isSubmitting) return true;
    if (state.step === DRSetupStep.WALLET) {
      const walletAddressState = getParameterStateForAddress(
        state.walletAddress.trim()
      );
      return !state.walletAddress.trim() || !walletAddressState.isValid;
    }
    return false;
  };

  const showBackButton = state.step > startStep;
  const progressConfig = getProgressConfig(state.step);

  // ---------------------------------------------------------------------------
  // Step content switch
  // ---------------------------------------------------------------------------

  const renderContent = () => {
    switch (state.step) {
      case DRSetupStep.INTRO:
        return <IntroContent />;
      case DRSetupStep.WHAT_IS_DR:
        return <WhatIsDRContent />;
      case DRSetupStep.HOW_DR_WORKS:
        return <HowDRWorksContent />;
      case DRSetupStep.RISKS:
        return <RisksContent />;
      case DRSetupStep.KEEP_SAFE:
        return <KeepSafeContent />;
      case DRSetupStep.HOW_TO_SETUP:
        return (
          <HowToSetupContent
            hasExistingProposal={hasExistingProposal}
            existingApprovalCount={existingApprovalCount}
            requiredApprovals={requiredApprovals}
            adminOperationTimelockDays={DEFAULT_ADMIN_OPERATION_TIMELOCK_DAYS}
          />
        );
      case DRSetupStep.START_SETUP:
        return <StartSetupContent />;
      case DRSetupStep.WALLET:
        return (
          <WalletContent
            walletAddress={state.walletAddress}
            onAddressChange={(address) =>
              dispatch({ type: 'SET_WALLET_ADDRESS', address })
            }
            walletHasActivity={walletHasActivity}
            walletActivityUnknown={walletActivityUnknown}
          />
        );
      case DRSetupStep.TIMELOCK:
        return (
          <TimelockContent
            timelockDays={state.timelockDays}
            onTimelockChange={(days) =>
              dispatch({ type: 'SET_TIMELOCK_DAYS', days })
            }
          />
        );
      case DRSetupStep.REVIEW:
        return (
          <ReviewContent
            walletAddress={state.walletAddress}
            timelockDays={state.timelockDays}
            numberOfApprovalsNeeded={numberOfApprovalsNeeded}
          />
        );
      default:
        return null;
    }
  };

  // Polled signables shaped for PendingSignaturesModal
  const polledSignables = polledProposal?.proposal
    ? [
        {
          id: polledProposal.proposal.id,
          approvals: polledProposal.approvals,
          rejections: polledProposal.rejections,
          requiredApprovals: polledProposal.requiredApprovals,
          initiatorSignature: undefined,
        },
      ]
    : [];

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleCloseAttempt();
        }}
        title={getStepTitle(state.step)}
        titleClassName="text-zinc-600 text-base text-center font-normal"
        noPadding
        headerClassName="bg-zinc-50 border-b border-zinc-200 h-[72px] px-[27px] py-[5px]"
        closeButtonClassName="size-12 rounded-full text-zinc-900 hover:bg-zinc-100 transition-colors"
        headerLeft={
          <div className="w-12 h-12 relative flex items-center justify-center">
            {showBackButton && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="absolute inset-0 size-12 rounded-full text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <ChevronLeft className="w-[18px] h-[18px]" />
              </Button>
            )}
          </div>
        }
        bodyContainerClassName="bg-zinc-50 p-0 flex flex-col"
        footer={
          <div className="flex gap-2.5 items-center justify-center w-full max-w-[700px]">
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg h-[37px] px-[19px]"
              onClick={() => setShowExitConfirm(true)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-lg h-[37px] px-[19px] gap-2.5"
              onClick={handleContinue}
              disabled={isContinueDisabled()}
            >
              {getContinueLabel()}
              <ArrowRight className="w-[15px] h-[15px]" />
            </Button>
          </div>
        }
        footerClassName="bg-zinc-100 border-t border-zinc-200 h-[74px] px-3 py-[10px]"
      >
        {/* Progress bar */}
        {progressConfig && (
          <DRProgressBar
            filledCount={progressConfig.filledCount}
            totalSegments={progressConfig.totalSegments}
          />
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-visible">
          <div className="flex flex-col items-center py-[50px] w-full">
            <div className="w-full max-w-[700px] px-4">{renderContent()}</div>
          </div>
        </div>
      </FullscreenDialog>

      {/* Confirmation dialogs — rendered outside the fullscreen panel for proper layering. */}
      <RisksConfirmDialog
        open={state.showRisksConfirmDialog}
        checked={state.risksChecked}
        onToggle={(i) => dispatch({ type: 'TOGGLE_RISKS_CHECKBOX', index: i })}
        onConfirm={() => dispatch({ type: 'CONFIRM_RISKS' })}
        onCancel={() => dispatch({ type: 'CLOSE_RISKS_CONFIRM' })}
      />

      <WalletSecurityConfirmDialog
        open={state.showWalletSecurityConfirmDialog}
        checked={state.walletSecurityChecked}
        onToggle={(i) =>
          dispatch({ type: 'TOGGLE_WALLET_SECURITY_CHECKBOX', index: i })
        }
        onConfirm={() => dispatch({ type: 'CONFIRM_WALLET_SECURITY' })}
        onCancel={() => dispatch({ type: 'CLOSE_WALLET_SECURITY_CONFIRM' })}
      />

      <ExitConfirmDialog
        open={showExitConfirm}
        onConfirm={() => {
          setShowExitConfirm(false);
          onClose();
        }}
        onCancel={() => setShowExitConfirm(false)}
      />

      <ExistingDRProposalDialog
        open={showExistingProposalDialog}
        onReviewProposal={() => {
          setShowExistingProposalDialog(false);
          handleReviewExistingProposal();
        }}
        onCancel={() => setShowExistingProposalDialog(false)}
      />

      {/* Mobile signing modal */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={handleSigningComplete}
        refreshSignables={refreshProposal}
        pendingSignableIds={pendingSignableIds}
        polledSignables={polledSignables}
        pendingDecision={pendingDecision}
        // Polling is handled by the parent hook via initiationRefreshInterval.
        hookFunctionToPoll={() => undefined}
        isTransaction={false}
      />

      {/* Success dialog */}
      <DRSuccessDialog
        open={showSuccess}
        requiredApprovals={numberOfApprovalsNeeded}
        onReturnToSettings={handleReturnToSettings}
      />
    </>
  );
}
